import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Mail, Phone, MapPin } from "lucide-react";

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={onClick}
        className="w-full py-4 flex items-start justify-between gap-4 text-left"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp size={20} className="text-[#3FA9F5] flex-shrink-0 mt-0.5" />
        ) : (
          <ChevronDown size={20} className="text-gray-400 flex-shrink-0 mt-0.5" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-600 text-sm leading-relaxed animate-fadeIn">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQsPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What is Fleety?",
      answer: "Fleety is a food delivery platform that helps you discover restaurants, place food orders, and get them delivered to your doorstep."
    },
    {
      question: "What charges are included in my order?",
      answer: (
        <div>
          <p className="mb-2">Your bill includes:</p>
          <p className="font-medium text-gray-700 mt-2 mb-1">Food Section:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Item Total (menu prices)</li>
            <li>Packing Charges (when enabled, shown at checkout)</li>
            <li>GST on Food & Packing (5%)</li>
          </ul>
          <p className="font-medium text-gray-700 mt-2 mb-1">Service Section:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Delivery Fee (based on distance)</li>
            <li>Platform Fee (₹20)</li>
            <li>GST on Delivery & Platform (18%)</li>
          </ul>
        </div>
      )
    },
    {
      question: "Why are there two different GST rates?",
      answer: "As per Indian GST regulations, food from restaurants attracts 5% GST, while delivery and platform services attract 18% GST. We display these separately for complete transparency and compliance."
    },
    {
      question: "What are packing charges?",
      answer: "Packing charges, when enabled, cover food-safe packaging materials used to keep your order fresh and secure. The current amount is shown separately before payment."
    },
    {
      question: "Are menu prices the same as restaurant prices?",
      answer: "Yes! Menu prices on Fleety match the restaurant's dine-in base prices. GST (5%) and any effective packing charges are shown separately in your bill for complete transparency."
    },
    {
      question: "Can you show me an example bill?",
      answer: (
        <div>
          <p className="mb-2">For a ₹40 food order with 1.3 km delivery:</p>
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between mb-1"><span>Item Total</span><span>₹40.00</span></div>
            <div className="flex justify-between mb-1"><span>Packing Charges</span><span>As shown at checkout</span></div>
            <div className="flex justify-between mb-1 font-medium border-t border-gray-200 pt-1"><span>Subtotal (Food + Packing)</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between mb-1"><span>GST on Food & Packing (5%)</span><span>5% of subtotal</span></div>
            <div className="flex justify-between mb-1 mt-2"><span>Delivery Fee (1.3 km)</span><span>₹40.00</span></div>
            <div className="flex justify-between mb-1"><span>Platform Fee</span><span>₹20.00</span></div>
            <div className="flex justify-between mb-1"><span>GST on Delivery & Platform (18%)</span><span>₹10.80</span></div>
            <div className="flex justify-between font-bold border-t border-gray-300 pt-2 mt-2"><span>To Pay</span><span>₹128.55</span></div>
          </div>
        </div>
      )
    },
    {
      question: "Are there any hidden charges?",
      answer: "No. All charges including Item Total, Packing Charges, GST, Delivery Fee, and Platform Fee are clearly shown before payment. The total amount displayed at checkout is the final payable amount."
    },
    {
      question: "When is my payment charged?",
      answer: "Your payment is charged immediately at the time you place the order through our secure payment gateway."
    },
    {
      question: "Can I cancel my order?",
      answer: "Once an order is placed and payment is completed, customer cancellation is not available. If the restaurant is unable to accept your order, the order is cancelled automatically and a refund is initiated. Orders may also be cancelled automatically due to system reasons (e.g., no rider available)."
    },
    {
      question: "What happens if the restaurant cancels or does not accept my order?",
      answer: "If the restaurant rejects your order, or does not respond within the acceptance window, your order is cancelled automatically and a full refund is initiated to your original payment method."
    },
    {
      question: "How long do refunds take?",
      answer: "Refund timelines depend on your bank or payment provider and typically take 5–7 business days after initiation."
    },
    {
      question: "Can I cancel after payment is processed?",
      answer: "No. Once payment is completed, customer cancellation is not permitted."
    },
    {
      question: "Who is responsible for food quality?",
      answer: (
        <div>
          <p className="mb-2">Restaurants are responsible for:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Food quality</li>
            <li>Hygiene</li>
            <li>Preparation accuracy</li>
          </ul>
          <p className="mt-2">If you face an issue, please contact Fleety support with your order details.</p>
        </div>
      )
    },
    {
      question: "How does delivery work?",
      answer: "Delivery is handled by independent delivery partners. Delivery times shown are estimates and may vary due to traffic or other conditions."
    },
    {
      question: "Can I track my order?",
      answer: "Yes. You can track your order status in real time through the Fleety app."
    },
    {
      question: "How can I rate or review my order?",
      answer: "After delivery, you can rate and review your experience with the restaurant and delivery partner."
    },
    {
      question: "What should I do if there's an issue with my order?",
      answer: "You can raise a dispute in the Fleety app using your order ID. For issues like missing items or incorrect food, submit supporting evidence (photos/videos) within 24 hours. Refunds are processed after verification as per policy."
    },
    {
      question: "When are refunds not provided?",
      answer: "Refunds are not provided for cancellations after pickup, disputes raised after 24 hours, incorrect delivery address, or if the customer is unavailable at delivery."
    },
    {
      question: "How do I contact Fleety support?",
      answer: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[#3FA9F5]" />
            <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5] hover:underline">support@fleety.co.in</a>
          </div>
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-[#3FA9F5]" />
            <a href="tel:+919845331817" className="text-[#3FA9F5] hover:underline">+91-98453 31817</a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#3FA9F5]" />
            <span>Bengaluru, India</span>
          </div>
        </div>
      )
    },
    {
      question: "Can Fleety suspend my account?",
      answer: "Fleety may suspend accounts in cases of misuse, fraud, or repeated policy violations."
    },
    {
      question: "What is Delivery OTP?",
      answer: "A 4-digit OTP is shared with you and must be given to the rider to confirm successful delivery. Orders cannot be marked delivered without OTP verification."
    },
    {
      question: "What is the dispute resolution timeline after evidence is submitted?",
      answer: "Disputes are reviewed and resolved within 5–7 business days after evidence submission."
    },
    {
      question: "What happens if evidence is inconclusive?",
      answer: "If the submitted evidence is inconclusive, Fleety will review available system logs and delivery records. In such cases, Fleety's decision will be final."
    },
    {
      question: "Is there an acceptance window for the restaurants for the order to be accepted?",
      answer: "Restaurants are required to accept orders within 5 minutes. Orders not accepted within this window are automatically cancelled and refunded."
    },
    {
      question: "What happens when there is no rider available after restaurant accepts?",
      answer: "If no rider is available after restaurant acceptance, Fleety operations will attempt manual rider assignment. If delivery is still not possible, the order will be cancelled and a full refund will be initiated."
    },
    {
      question: "What happens when the wrong OTP is provided by the customer and is entered multiple times by the rider?",
      answer: "There is no penalty for incorrect OTP attempts. However, the order cannot be completed without correct OTP verification."
    },
    {
      question: "What happens to food if OTP not provided by the customer?",
      answer: "If the correct OTP is not provided, the order will not be marked as delivered and no refund will be issued. Fleety will not be responsible for the food in such cases."
    },
    {
      question: "Can OTP be regenerated by the customer?",
      answer: "No. OTPs cannot be regenerated."
    },
    {
      question: "What is the platform fee?",
      answer: "Fleety charges a platform fee of ₹20 + 18% GST (₹3.60) per order. This is shown clearly at checkout."
    },
    {
      question: "How do I find my order ID?",
      answer: "Your 8-digit Order ID is generated after placing an order and is visible in the app and order confirmation screen."
    },
    {
      question: "Can I order from multiple restaurants at once?",
      answer: "No. Each order can be placed with only one restaurant at a time."
    },
    {
      question: "What if the rider doesn't arrive?",
      answer: "If a rider is unable to reach the delivery location and delivery cannot be completed, the order will be cancelled and a full refund will be initiated."
    },
    {
      question: "How can I delete my account?",
      answer: (
        <div>
          <p className="mb-2">To delete your Fleety account:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Open the Fleety app and go to "My Fleety" menu</li>
            <li>Tap on "Delete my account" option (below Logout)</li>
            <li>Confirm your request in the warning dialog</li>
            <li>Your deletion request will be processed within 5-7 business days</li>
          </ol>
          <p className="mt-3 text-orange-600 text-sm font-medium">⚠️ Please note: Account deletion is permanent and cannot be undone. All your data including order history, saved addresses, and preferences will be permanently deleted.</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="faqs-page">
      {/* Header */}
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button 
          onClick={() => navigate(-1)}
          className="text-white hover:text-white/80 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">FAQs</h1>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Fleety – Customer FAQs</h1>
          <p className="text-sm text-gray-500 mb-6">Frequently Asked Questions</p>

          <div className="divide-y divide-gray-100">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-8 p-4 bg-[#3FA9F5]/5 rounded-xl">
            <h3 className="font-semibold text-gray-900 mb-2">Still have questions?</h3>
            <p className="text-sm text-gray-600 mb-3">
              Can't find the answer you're looking for? Please contact our support team.
            </p>
            <div className="space-y-2">
              <a 
                href="mailto:support@fleety.co.in"
                className="inline-flex items-center gap-2 text-[#3FA9F5] font-medium hover:underline"
              >
                <Mail size={18} />
                support@fleety.co.in
              </a>
              <div className="flex items-center gap-2 text-[#3FA9F5] font-medium">
                <Phone size={18} />
                <a href="tel:+919845331817" className="hover:underline">+91-98453 31817</a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQsPage;
